import { useState, useEffect } from "react";
import { Link, NavLink, useLocation, Outlet } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { SITE, NAV, SERVICES } from "@/data/site";
import MusicToggle from "@/components/MusicToggle";
import { StickyCall } from "@/components/StickyCall";

const Header = () => {
  const [open, setOpen] = useState(false);
  const [svcOpen, setSvcOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => { setOpen(false); setSvcOpen(false); }, [loc.pathname]);

  return (
    <header className="sticky top-0 z-30 border-b border-border bg-linen/80 backdrop-blur-lg">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
        <Link to="/" data-testid="brand-logo" className="font-serif text-2xl leading-none tracking-tight text-ink">
          Let Go <span className="italic text-clay">Wellness</span>
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          <div className="relative" onMouseEnter={() => setSvcOpen(true)} onMouseLeave={() => setSvcOpen(false)}>
            <button data-testid="nav-services" className="flex items-center gap-1 font-sans text-sm text-ink-soft transition-colors hover:text-ink">
              Services <ChevronDown size={14} />
            </button>
            {svcOpen && (
              <div className="absolute left-1/2 top-full w-72 -translate-x-1/2 border border-border bg-white p-2 shadow-[0_8px_30px_rgb(0,0,0,0.06)]">
                {SERVICES.map((s) => (
                  <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`nav-service-${s.slug}`}
                    className="block px-4 py-2.5 font-sans text-sm text-ink-soft transition-colors hover:bg-linen hover:text-ink">
                    {s.short}
                  </Link>
                ))}
              </div>
            )}
          </div>
          {NAV.filter((n) => n.key !== "services").map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.replace(/\s+/g, "-").toLowerCase()}`}
              className={({ isActive }) => `font-sans text-sm transition-colors hover:text-ink ${isActive ? "text-ink" : "text-ink-soft"}`}>
              {n.label}
            </NavLink>
          ))}
          <a href={`tel:${SITE.phoneOffice}`} data-testid="header-call"
            className="inline-flex items-center gap-1.5 font-sans text-sm text-ink transition-colors hover:text-clay">
            <Phone size={15} /> {SITE.phoneOffice}
          </a>
          <Link to="/new-patient" data-testid="header-cta"
            className="rounded-full bg-ink px-5 py-2.5 font-sans text-sm text-linen transition-colors hover:bg-ink/85">
            New Patient
          </Link>
        </nav>

        <button data-testid="mobile-menu-toggle" className="md:hidden text-ink" onClick={() => setOpen(!open)} aria-label="Menu">
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="border-t border-border bg-linen px-6 py-4 md:hidden">
          {SERVICES.map((s) => (
            <Link key={s.slug} to={`/services/${s.slug}`} className="block py-2 font-sans text-sm text-ink-soft">{s.short}</Link>
          ))}
          {NAV.filter((n) => n.key !== "services").map((n) => (
            <Link key={n.to} to={n.to} className="block py-2 font-sans text-sm text-ink-soft">{n.label}</Link>
          ))}
          <Link to="/new-patient" data-testid="mobile-cta" className="mt-3 block rounded-full bg-ink px-5 py-2.5 text-center font-sans text-sm text-linen">New Patient</Link>
        </div>
      )}
    </header>
  );
};

const Footer = () => (
  <footer className="border-t border-border bg-white">
    <div className="mx-auto grid max-w-6xl gap-10 px-6 py-16 md:grid-cols-4">
      <div className="md:col-span-1">
        <div className="font-serif text-2xl text-ink">Let Go <span className="italic text-clay">Wellness</span></div>
        <p className="mt-3 font-sans text-sm leading-relaxed text-muted-foreground">Compassionate mental health therapy in Waterbury, CT. In-person & telehealth across Connecticut.</p>
      </div>
      <div>
        <div className="overline mb-4">Care</div>
        {SERVICES.map((s) => (
          <Link key={s.slug} to={`/services/${s.slug}`} className="block py-1 font-sans text-sm text-ink-soft transition-colors hover:text-ink">{s.short}</Link>
        ))}
      </div>
      <div>
        <div className="overline mb-4">Practice</div>
        <Link to="/about" className="block py-1 font-sans text-sm text-ink-soft hover:text-ink">Our Approach</Link>
        <Link to="/therapists/waleska-castillo" className="block py-1 font-sans text-sm text-ink-soft hover:text-ink">Meet Waleska</Link>
        <Link to="/insurance" className="block py-1 font-sans text-sm text-ink-soft hover:text-ink">Insurance & Fees</Link>
        <Link to="/blog" className="block py-1 font-sans text-sm text-ink-soft hover:text-ink">Journal</Link>
        <Link to="/new-patient" className="block py-1 font-sans text-sm text-ink-soft hover:text-ink">New Patient</Link>
        <Link to="/contact" className="block py-1 font-sans text-sm text-ink-soft hover:text-ink">Contact & Visit</Link>
      </div>
      <div>
        <div className="overline mb-4">Visit</div>
        <address className="font-sans text-sm not-italic leading-relaxed text-ink-soft" data-testid="footer-nap">
          {SITE.street}<br />{SITE.city}, {SITE.state} {SITE.zip}<br />
          <a href={`tel:${SITE.phoneOffice}`} className="hover:text-ink">{SITE.phoneOffice}</a><br />
          <a href={`mailto:${SITE.email}`} className="hover:text-ink">{SITE.email}</a>
        </address>
      </div>
    </div>
    <div className="border-t border-border">
      <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-2 px-6 py-5 md:flex-row">
        <p className="font-sans text-xs text-muted-foreground">© {new Date().getFullYear()} Let Go Wellness. HIPAA-protected & confidential.</p>
        <div className="flex items-center gap-5">
          <p className="font-sans text-xs text-clay"><Phone size={11} className="mr-1 inline" />In crisis? Call or text 988 — free, 24/7.</p>
          <Link to="/admin/login" data-testid="footer-admin-link" className="font-sans text-xs text-muted-foreground underline-offset-4 transition-colors hover:text-ink hover:underline">Admin</Link>
        </div>
      </div>
    </div>
  </footer>
);

export default function Layout() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return (
    <div className="flex min-h-screen flex-col bg-linen">
      <Header />
      <main className="flex-1 pb-14 md:pb-0"><Outlet /></main>
      <Footer />
      <MusicToggle />
      <StickyCall />
    </div>
  );
}
